/**
 * PoliciesShortcut — sidebar widget on the Bulletin board.
 * Shows a policies count + "N pending your acknowledgement" and a shortcut.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ScrollText, ChevronRight } from "lucide-react";

export default function PoliciesShortcut({ wsId }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/policies/summary`)
      .then((r) => setSummary(r.data))
      .catch(() => {});
  }, [wsId]);

  if (!summary || summary.published_count === 0) return null;
  const hasPending = summary.pending_ack_for_me > 0;
  return (
    <section className={`border ${hasPending ? "border-amber-400" : "border-border"}`}
             data-testid="widget-policies-shortcut">
      <div className={`px-4 py-3 border-b border-border flex items-center gap-2 ${
        hasPending ? "bg-amber-50" : "bg-primary/5"
      }`}>
        <ScrollText className="w-4 h-4" />
        <div className="label-eyebrow">Company policies</div>
        <Link to="/app/policies" className="ml-auto text-xs underline">All →</Link>
      </div>
      <div className="px-4 py-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{summary.published_count} published</span>
          {hasPending && (
            <Link to="/app/policies?filter=needs-ack"
                   className="text-amber-700 font-semibold hover:underline"
                   data-testid="policies-pending-ack-link">
              {summary.pending_ack_for_me} pending your acknowledgement →
            </Link>
          )}
        </div>
      </div>
      <div className="divide-y divide-border">
        {(summary.latest || []).slice(0, 4).map((p) => (
          <Link key={p.id} to={`/app/policies/${p.id}`}
                 className="px-4 py-2 text-xs flex items-center gap-2 hover:bg-muted/40"
                 data-testid={`policies-shortcut-item-${p.id}`}>
            <span
              className="text-[9px] uppercase font-bold text-slate-500 tracking-widest shrink-0 max-w-[80px] truncate"
              title={p.category}
            >
              {p.category}
            </span>
            <span className="flex-1 truncate min-w-0">{p.title}</span>
            <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
          </Link>
        ))}
      </div>
    </section>
  );
}
